import type { ApiTagType } from "_shared/_stores/api";

/** What a list's counts query hook answers that `useListCounts` reads. */
interface CountsQueryResult<TCounts> {
  data?: TCounts;
  isLoading: boolean;
  isError: boolean;
}

/** A list's counts query hook, as RTK Query generates it for a counts endpoint. */
export type CountsQueryHook<TArgs, TCounts> = (
  args: TArgs,
  options: { skip: boolean },
) => CountsQueryResult<TCounts>;

/** Every badge and chip count of one list, from its one counts request (#6709). */
export interface ListCounts<TCounts> {
  /** The counts, kept while a newer selection's are on their way; undefined until the first arrive. */
  counts: TCounts | undefined;
  /** True while the first counts are on their way. */
  isLoading: boolean;
  isError: boolean;
}

/**
 * The cache tags a list's counts endpoint provides: its list's own `LIST` tag first, so the Ably
 * invalidation and the mutations that refetch the list refetch its counts too (#6709).
 */
export function listCountsTags<TTagType extends ApiTagType>(
  type: TTagType,
  ...alsoIds: string[]
): { type: TTagType; id: string }[] {
  return [{ type, id: "LIST" }, ...alsoIds.map((id) => ({ type, id }))];
}

/**
 * The hook that reads every badge and chip count of one list, with one request a page load (#6709).
 *
 * Each list has one counts endpoint that answers its tab badges and chip counts in one statement,
 * taking the list's own criteria. A chip's count is what that chip lists with the other groups'
 * selection, so the chips of a list are always counted together, and a page never asks once per
 * chip. Skipped, the hook reads as no counts rather than as zeros.
 *
 * Built once per list at module scope, beside the counts endpoint: a hook handed to a component as
 * a value is one the React Compiler cannot prove stable, and it gives up on the whole component.
 */
export function listCountsHook<TArgs, TCounts>(
  useCountsQuery: CountsQueryHook<TArgs, TCounts>,
): (args: TArgs, options?: { skip?: boolean }) => ListCounts<TCounts> {
  return function useListCounts(args: TArgs, options: { skip?: boolean } = {}): ListCounts<TCounts> {
    const skip = options.skip === true;
    const { data, isLoading, isError } = useCountsQuery(args, { skip });
    if (skip) return { counts: undefined, isLoading: false, isError: false };
    return { counts: data, isLoading, isError };
  };
}
